/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, DateField, Text, TextInput } from '@/shared/ui';
import { formatStatsCurrency } from '@/shared/utils/format-stats';

import { MicroLabel, PanelEmptyState, SurfaceCard, formatCalendarDate } from './sale-detail-atoms';
import type { CommissionReceipt, SaleCommissionPanel } from '../types/sale.types';

export interface SaleCommissionSectionProps {
  panel: SaleCommissionPanel | null;
  receipts: CommissionReceipt[];
  onRecord: (receipt: CommissionReceipt) => Promise<void>;
  /** Terminal sales no longer take new receipts. */
  readOnly: boolean;
}

/**
 * Expected vs received commission, the receipts behind it, and the form to
 * record one more.
 *
 * Every figure comes from the backend's derivation — nothing is computed
 * twice. `outstanding` is the gap between what the negotiated percentage
 * implies and what was recorded as arriving; it is a reconciliation number
 * the broker reads, never a debt the system pursues (project rule 12). The
 * form records a fact that already happened: it does not compute anyone's
 * share.
 *
 * @param props.panel - The derived figures.
 * @param props.receipts - What has been recorded so far.
 * @param props.onRecord - Appends a receipt.
 * @param props.readOnly - Whether the sale has reached a terminal state.
 * @returns The rendered commission section.
 */
export function SaleCommissionSection({
  panel,
  receipts,
  onRecord,
  readOnly,
}: SaleCommissionSectionProps) {
  const { t } = useTranslation('features/sales');
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [receivedAt, setReceivedAt] = useState('');
  const [busy, setBusy] = useState(false);

  const canSubmit = Boolean(amount && receivedAt) && !busy;
  const outstanding = panel?.outstanding ?? 0;

  async function submit() {
    setBusy(true);
    try {
      await onRecord({
        recipient: recipient.trim(),
        amount: Number(amount),
        received_at: receivedAt,
      });
      setRecipient('');
      setAmount('');
      setReceivedAt('');
      toast.success(t('commission.recorded'));
    } catch {
      toast.error(t('commission.recordFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-(--space-3)">
      <SurfaceCard
        testId="sale-commission-summary"
        className="flex flex-col gap-(--space-3)"
      >
        <MicroLabel>{t('detail.tabs.commission')}</MicroLabel>
        <div className="grid gap-(--space-3) [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]">
          <Figure label={t('commission.expected')} value={panel?.expected} />
          <Figure
            label={t('commission.received')}
            value={panel?.received}
            accent="var(--accent-salvia)"
          />
          <Figure
            label={t('commission.outstanding')}
            value={panel?.outstanding}
            accent={outstanding > 0 ? 'var(--accent-mostarda)' : undefined}
          />
        </div>
        <Text component="span" variant="meta" tone="tertiary" className="leading-relaxed">
          {t('commission.summaryNote')}
        </Text>
      </SurfaceCard>

      <SurfaceCard
        testId="sale-commission-receipts"
        className="flex flex-col gap-(--space-2)"
      >
        <MicroLabel>{t('commission.receiptsTitle')}</MicroLabel>
        {receipts.length === 0 ? (
          <PanelEmptyState
            message={t('commission.noReceipts')}
            hint={t('commission.noReceiptsHint')}
          />
        ) : (
          <div className="flex flex-col">
            {receipts.map((receipt, index) => (
              <div
                key={`${receipt.recipient}-${receipt.received_at}-${index}`}
                data-testid="commission-receipt-row"
                className="flex items-center justify-between gap-(--space-3) border-b border-(--border-subtle) py-(--space-2) last:border-b-0"
              >
                <div className="flex min-w-0 flex-col gap-(--space-0-5)">
                  <Text
                    component="span"
                    variant="bodySm"
                    fw={500}
                    tone="primary"
                    className="truncate"
                  >
                    {receipt.recipient || t('commission.unnamedRecipient')}
                  </Text>
                  <Text component="span" variant="meta" tone="tertiary" className="tabular-nums">
                    {formatCalendarDate(receipt.received_at)}
                  </Text>
                </div>
                <Text
                  component="span"
                  variant="bodySm"
                  fw={600}
                  style={{ color: 'var(--accent-salvia)' }}
                  className="shrink-0 tabular-nums"
                >
                  {formatStatsCurrency(receipt.amount)}
                </Text>
              </div>
            ))}
          </div>
        )}
      </SurfaceCard>

      {!readOnly && (
        <SurfaceCard
          testId="sale-commission-form"
          className="flex flex-col gap-(--space-3)"
        >
          <MicroLabel>{t('commission.formTitle')}</MicroLabel>
          <div className="grid items-end gap-(--space-2) [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
            <TextInput
              label={t('commission.recipient')}
              data-testid="receipt-recipient-input"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
            />
            <TextInput
              label={t('commission.amount')}
              type="number"
              step="0.01"
              min="0"
              data-testid="receipt-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <DateField
              label={t('commission.receivedAt')}
              data-testid="receipt-date-input"
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
            />
            <Button
              size="sm"
              variant="solid"
              disabled={!canSubmit}
              loading={busy}
              data-testid="receipt-submit-button"
              onClick={() => void submit()}
            >
              {busy ? t('commission.recording') : t('commission.record')}
            </Button>
          </div>
        </SurfaceCard>
      )}
    </div>
  );
}

/**
 * One derived currency figure.
 *
 * @param props.label - The figure's label.
 * @param props.value - The amount, or undefined while it loads.
 * @param props.accent - Optional accent token for the amount.
 * @returns The rendered figure.
 */
function Figure({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | undefined;
  accent?: string;
}) {
  return (
    <div className="flex flex-col gap-(--space-0-5)">
      <Text component="span" variant="meta" tone="tertiary">
        {label}
      </Text>
      <Text
        component="span"
        fw={600}
        tone={accent ? undefined : 'primary'}
        style={accent ? { color: accent } : undefined}
        className="text-[length:var(--text-lg)] tabular-nums"
      >
        {value === undefined ? '—' : formatStatsCurrency(value)}
      </Text>
    </div>
  );
}

export default SaleCommissionSection;
